import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiPercent, FiActivity, FiDollarSign, FiTrendingUp, FiBarChart2, FiClock, FiExternalLink } from 'react-icons/fi';
import { reportAPI } from '../../services/api';
import StatCard from '../../components/shared/StatCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const ReportPage = () => {
  const navigate = useNavigate();
  const [occupancy, setOccupancy] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [summary, setSummary] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [tab, setTab] = useState('overview');

  // Rapport financier mandats
  const [agencyMandates, setAgencyMandates] = useState(null);
  const [mandatesLoading, setMandatesLoading] = useState(false);
  const [mandateStatusFilter, setMandateStatusFilter] = useState('active');

  useEffect(() => {
    Promise.all([
      reportAPI.getOccupancyRate(),
      reportAPI.getRevenueChart({ months: 12 }),
      reportAPI.getTransactionSummary(),
      reportAPI.getActivityLogs({ limit: 20 }),
    ]).then(([occ, rev, sum, act]) => {
      setOccupancy(occ.data);
      setRevenue(rev.data.map(r => ({ month: new Date(r.month).toLocaleDateString('fr-FR', { month: 'short' }), Revenus: parseFloat(r.income), Dépenses: parseFloat(r.expense) })));
      setSummary(sum.data);
      setActivityLogs(act.data);
    }).catch(() => {});
  }, []);

  const loadMandatesFinancial = (status) => {
    setMandatesLoading(true);
    setMandateStatusFilter(status);
    reportAPI.getAgencyMandatesFinancial({ status })
      .then(r => setAgencyMandates(r.data))
      .catch(() => {})
      .finally(() => setMandatesLoading(false));
  };

  useEffect(() => {
    if (tab === 'mandats') {
      loadMandatesFinancial(mandateStatusFilter);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const pieColors = ['#22c55e', '#ef4444', '#3b82f6', '#f59e0b'];
  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

  const TABS = [
    { key: 'overview', label: '📊 Vue générale' },
    { key: 'revenus',  label: '💹 Revenus' },
    { key: 'mandats',  label: '🏠 Mandats & Commissions' },
    { key: 'activité', label: '📋 Activité' },
  ];

  return (
    <div>
      <div className="page-header"><h1>Rapports & Analytics</h1></div>

      <div className="tabs">
        {TABS.map(t => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Vue générale ── */}
      {tab === 'overview' && (
        <>
          <div className="stats-grid">
            {occupancy && (
              <>
                <StatCard icon={<FiHome />} label="Biens en location" value={occupancy.total} />
                <StatCard icon={<FiHome />} label="Biens occupés" value={occupancy.occupied} color="#22c55e" bgColor="#dcfce7" />
                <StatCard icon={<FiPercent />} label="Taux d'occupation" value={`${occupancy.rate}%`} color={occupancy.rate >= 70 ? '#22c55e' : '#f59e0b'} bgColor={occupancy.rate >= 70 ? '#dcfce7' : '#fffbeb'} />
              </>
            )}
          </div>

          {summary.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header"><h2>Résumé des transactions</h2></div>
              <div className="card-body" style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={summary.map(s => ({ name: s.type, value: parseFloat(s.total) }))} cx="50%" cy="50%" outerRadius={100} dataKey="value" label>
                      {summary.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${parseInt(v).toLocaleString()} XOF`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Revenus ── */}
      {tab === 'revenus' && (
        <div className="card">
          <div className="card-header"><h2>Revenus vs Dépenses (12 mois)</h2></div>
          <div className="card-body" style={{ height: 400 }}>
            {revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(v) => `${parseInt(v).toLocaleString()} XOF`} />
                  <Legend />
                  <Bar dataKey="Revenus" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><h3>Aucune donnée</h3></div>}
          </div>
        </div>
      )}

      {/* ── Mandats & Commissions ── */}
      {tab === 'mandats' && (
        <>
          {/* KPIs globaux */}
          {agencyMandates && (
            <div className="grid-4" style={{ marginBottom: 24 }}>
              {[
                { icon: <FiDollarSign />, label: 'Total loyers collectés', value: fmt(agencyMandates.totals.total_collected) + ' XOF', color: '#22c55e', bg: '#dcfce7' },
                { icon: <FiTrendingUp />, label: 'Commissions agence', value: fmt(agencyMandates.totals.agency_commission) + ' XOF', color: '#3b82f6', bg: '#dbeafe' },
                { icon: <FiBarChart2 />, label: 'Net propriétaires', value: fmt(agencyMandates.totals.owner_net) + ' XOF', color: '#7c3aed', bg: '#ede9fe' },
                { icon: <FiClock />, label: 'Loyers en attente', value: fmt(agencyMandates.totals.total_pending) + ' XOF', color: agencyMandates.totals.total_overdue > 0 ? '#ef4444' : '#f59e0b', bg: agencyMandates.totals.total_overdue > 0 ? '#fee2e2' : '#fffbeb' },
              ].map((kpi, i) => (
                <div key={i} className="card">
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, fontSize: 20, flexShrink: 0 }}>
                      {kpi.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{kpi.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtre statut */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { key: 'active',    label: 'Actifs' },
              { key: 'signed',    label: 'Signés' },
              { key: 'expired',   label: 'Expirés' },
              { key: 'cancelled', label: 'Annulés' },
            ].map(s => (
              <button
                key={s.key}
                className={`btn btn-sm ${mandateStatusFilter === s.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => loadMandatesFinancial(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Tableau des mandats */}
          <div className="card">
            <div className="card-header">
              <h2>État financier par propriété ({mandateStatusFilter})</h2>
            </div>
            {mandatesLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : !agencyMandates ? null : (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Bien</th>
                      <th>Propriétaire</th>
                      <th>N° Mandat</th>
                      <th style={{ textAlign: 'center' }}>Commission</th>
                      <th style={{ textAlign: 'right' }}>Loyers collectés</th>
                      <th style={{ textAlign: 'right' }}>Commission agence</th>
                      <th style={{ textAlign: 'right' }}>Net propriétaire</th>
                      <th style={{ textAlign: 'right' }}>En attente</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {agencyMandates.mandates.map(m => (
                      <tr key={m.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{m.property_title || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.property_city || ''}</div>
                        </td>
                        <td>
                          <div>{m.client_first_name} {m.client_last_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.client_email || ''}</div>
                        </td>
                        <td><code style={{ fontSize: 12 }}>{m.mandate_number}</code></td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 15 }}>{m.commission_rate || 0}%</span>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>
                          {m.financial ? fmt(m.financial.total_collected) + ' XOF' : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: '#3b82f6' }}>
                          {m.financial ? fmt(m.financial.agency_commission) + ' XOF' : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#7c3aed' }}>
                          {m.financial ? fmt(m.financial.owner_net) + ' XOF' : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: m.financial?.total_overdue > 0 ? '#ef4444' : '#f59e0b' }}>
                          {m.financial ? fmt(m.financial.total_pending) + ' XOF' : '—'}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => navigate(`/mandates/${m.id}`)}
                            title="Voir le mandat"
                          >
                            <FiExternalLink />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {agencyMandates.mandates.length === 0 && (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>
                          Aucun mandat trouvé pour ce statut
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Activité ── */}
      {tab === 'activité' && (
        <div className="card">
          <div className="card-header"><h2>Journal d'activité</h2></div>
          <div className="card-body">
            {activityLogs.map(log => (
              <div key={log.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'center' }}>
                <FiActivity color="var(--primary)" />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>{log.first_name} {log.last_name}</span>
                  <span style={{ color: 'var(--text-secondary)', margin: '0 8px' }}>-</span>
                  <span>{log.description || log.type}</span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(log.created_at).toLocaleString('fr-FR')}</span>
              </div>
            ))}
            {activityLogs.length === 0 && <div className="empty-state"><h3>Aucune activité</h3></div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
